import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Get session overrides for a specific date
 * @param {string} userId
 * @param {string} date - yyyy-mm-dd format
 * @returns {Promise<object|null>}
 */
export const getSessionOverride = async (userId, date) => {
  try {
    const overrideRef = doc(db, 'users', userId, 'sessionOverrides', date);
    const overrideDoc = await getDoc(overrideRef);

    if (overrideDoc.exists()) {
      return overrideDoc.data();
    }
    return null;
  } catch (error) {
    console.error('Error getting session override:', error);
    return null;
  }
};

/**
 * Add an exercise to today's session
 * @param {string} userId
 * @param {string} date - yyyy-mm-dd format
 * @param {string} focus - session focus to guard against plan changes
 * @param {string} exerciseId
 * @param {number} afterIndex - insert after this exercise index (-1 for start)
 */
export const addExerciseToSession = async (userId, date, focus, exerciseId, afterIndex) => {
  try {
    const overrideRef = doc(db, 'users', userId, 'sessionOverrides', date);
    const overrideDoc = await getDoc(overrideRef);

    let added = [];

    if (overrideDoc.exists()) {
      const data = overrideDoc.data();
      added = data.added || [];
    }

    // Add the new exercise
    added.push({ exerciseId, afterIndex });

    await setDoc(overrideRef, {
      focus,
      added,
      removed: [],
      updatedAt: serverTimestamp(),
    });

    return true;
  } catch (error) {
    console.error('Error adding exercise to session:', error);
    throw error;
  }
};

/**
 * Apply overrides to a generated session
 * @param {object} session - generated session from buildSession
 * @param {object} override - override data from sessionOverrides
 * @param {object} library - exercises.json data
 * @returns {object} session with overrides applied
 */
export const applySessionOverride = (session, override, library) => {
  if (!override || !override.added || override.added.length === 0) {
    return session;
  }

  const exercises = [...session.exercises];
  const byId = new Map(library.map((e) => [e.id, e]));

  // Apply additions
  override.added.forEach(({ exerciseId, afterIndex }) => {
    const exerciseData = byId.get(exerciseId);
    if (!exerciseData) return;

    const exercise = {
      exerciseId: exerciseData.id,
      name: exerciseData.name,
      sets: exerciseData.defaultSets,
      reps: exerciseData.defaultReps,
      restSeconds: exerciseData.defaultRestSeconds,
      movementPattern: exerciseData.movementPattern,
      muscleGroup: exerciseData.muscleGroup,
      minTier: exerciseData.minTier,
      substitutes: exerciseData.substitutes,
      isAdded: true, // flag to identify added exercises
    };

    // Insert after the specified index
    const insertIndex = afterIndex + 1;
    if (insertIndex >= 0 && insertIndex <= exercises.length) {
      exercises.splice(insertIndex, 0, exercise);
    }
  });

  return {
    ...session,
    exercises,
  };
};
